import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import SaveIcon from '@mui/icons-material/Save';
import { Button, styled, Switch } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { useTypedSelector } from 'hooks';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { auth, cleanAuth, toggleEditMode } from 'store';
import { API } from '_helpers';
import { AddButton, DeleteButton } from './components';
import { TechsViewMode } from './components/techs';
import Techs from './components/techs/Techs';
import { marks, techsList } from './data';

const Root = styled(Stack)(({ theme }) => ({
  '::-webkit-scrollbar': {
    display: 'none',
  },
  '&': {
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
  },
  position: 'relative',
}));

interface IPortfolio {
  link: string | null;
  description: string | null;
}

const Projects: React.FC<IProps<IDeveloper>> = ({ data }) => {
  const { locale } = useRouter();
  const stack = data.stack as any;
  const dispatch = useDispatch();

  const [current, setCurrent] = React.useState('');

  const { loading, error, available, hours, ...rest } = useTypedSelector(
    (state) => state.auth
  ) as any;

  const { modeEditStack, modeEditPortfolio, modeEditAvailability } =
    useTypedSelector((state) => state.toggleEditMode);

  const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      auth({
        loading,
        error,
        hours,
        available: event.target.checked,
        ...rest,
      })
    );
  };
  const [portfolio, setPortfolio] = React.useState<IPortfolio[]>([
    {
      link: null,
      description: null,
    },
  ]);

  const handleChange = (event: Event, newValue: number | number[]) => {
    event.preventDefault();
    dispatch(
      auth({
        loading,
        error,
        hours: newValue,
        available,
        ...rest,
      })
    );
  };

  const handlePortfolioChange =
    (index: number, name: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();
      setPortfolio((portfolio) =>
        portfolio.map((el, i) => {
          return i === index ? { ...el, [name]: event?.target.value } : el;
        })
      );
    };

  const addToPortfolio = () => {
    setPortfolio((portfolio) => [
      ...portfolio,
      {
        link: null,
        description: null,
      },
    ]);
  };

  const removeToPortfolio = (index: number) => {
    setPortfolio((prev) => {
      const test = prev.filter((el) => prev.indexOf(el) !== index);
      return test;
    });
  };

  React.useEffect(() => {
    setPortfolio(data?.portfolio as any);
    dispatch(
      auth({
        mobile: data?.stack?.mobile,
        frontend: data?.stack?.frontend,
        backend: data?.stack?.backend,
        database: data?.stack?.database,
        others: data?.stack?.others,
        choice: data?.stack?.choice,
        anything: data?.stack?.anything,
        available: data?.availability?.available || false,
        hours: data?.availability?.hours
          ? (data?.availability?.hours - 1) * 12.5
          : 37.5,
        ...rest,
      })
    );
    return (): void => {
      dispatch(cleanAuth());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async () => {
    try {
      dispatch(auth({ loading: true, error: '' }));
      setCurrent('stack');
      const { mobile, frontend, backend, database, others, choice, anything } =
        rest;
      const res = await axios.put(
        `${API}/developers/${data._id}`,
        {
          stack: {
            mobile,
            frontend,
            backend,
            database,
            others,
            choice,
            anything,
          },
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${data.jwt}`,
          },
        }
      );
      setCurrent('');
      dispatch(toggleEditMode({ modeEditStack: false }));
      dispatch(
        auth({
          ...rest,
          loading: false,
          mobile: res?.data?.stack?.mobile,
          frontend: res?.data?.stack?.frontend,
          backend: res?.data?.stack?.backend,
          database: res?.data?.stack?.database,
          others: res?.data?.stack?.others,
          choice: res?.data?.stack?.choice,
          anything: res?.data?.stack?.anything,
        })
      );
    } catch (error: any) {
      return dispatch(
        auth({
          loading: false,
          error: 'Error',
        })
      );
    }
  };

  const onSubmitPortfolio = async () => {
    try {
      dispatch(auth({ loading: true, error: '' }));
      setCurrent('portfolio');
      const res = await axios.put(
        `${API}/developers/${data._id}`,
        {
          portfolio,
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${data.jwt}`,
          },
        }
      );
      setCurrent('');
      dispatch(toggleEditMode({ modeEditPortfolio: false }));
      dispatch(auth({ loading: false, ...rest }));
    } catch (error: any) {
      return dispatch(
        auth({
          loading: false,
          error: error.response.data,
        })
      );
    }
  };

  const onSubmitAvailability = async () => {
    try {
      dispatch(auth({ loading: true, error: '' }));
      setCurrent('availability');
      const res = await axios.put(
        `${API}/developers/${data._id}`,
        {
          availability: {
            available,
            hours: hours / 12.5 + 1,
          },
        },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${data.jwt}`,
          },
        }
      );
      setCurrent('');
      dispatch(toggleEditMode({ modeEditAvailability: false }));
      dispatch(auth({ loading: false }));
    } catch (error: any) {
      return dispatch(
        auth({
          loading: false,
          error: error.response.data,
        })
      );
    }
  };

  return (
    <Root spacing={6}>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={6}>
          {!modeEditStack
            ? techsList.map((el, i) => (
                <TechsViewMode
                  key={el.title}
                  {...el}
                  value={rest && rest[el.name]}
                />
              ))
            : techsList.map((el, i) => (
                <Techs key={el.title} {...el} value={rest && rest[el.name]} />
              ))}
          {modeEditStack ? (
            <Box>
              <Button
                variant="contained"
                disableElevation
                onClick={onSubmit}
                startIcon={<SaveIcon />}
              >
                {current === 'stack' && loading
                  ? locale === 'fr'
                    ? 'Chargement...'
                    : 'Loading...'
                  : locale === 'fr'
                  ? 'Enregistrer'
                  : 'Save'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Button
                disableElevation
                onClick={() =>
                  dispatch(toggleEditMode({ modeEditStack: true }))
                }
                startIcon={<EditIcon />}
              >
                {locale === 'fr' ? 'Editer' : 'Edit'}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Stack spacing={4}>
          <Stack spacing={2} direction="row" alignItems="center">
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" id="portfolio" color="primary">
              Portfolio
            </Typography>
            <Typography component="em" color="text.secondary" fontSize={13}>
              (
              {locale === 'fr'
                ? 'Ajouter des projets sur lesquels vous avez travaillé dans le passé'
                : 'Add projects you have worked on in the past'}
              )
            </Typography>
          </Stack>

          {modeEditPortfolio
            ? portfolio.map((el, i) => (
                <Stack spacing={2} key={i}>
                  <DeleteButton handleClick={() => removeToPortfolio(i)} />
                  <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
                    <InputBase
                      sx={{ width: '100%' }}
                      placeholder={
                        locale === 'fr'
                          ? 'Lien vers le projet'
                          : 'Link to project'
                      }
                      value={el.link}
                      onChange={handlePortfolioChange(i, 'link')}
                      startAdornment={
                        <InputAdornment position="start">
                          <InsertLinkIcon />
                        </InputAdornment>
                      }
                    />
                  </Paper>
                  <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
                    <InputBase
                      sx={{ width: '100%' }}
                      placeholder={
                        locale === 'fr'
                          ? 'Brève description du projet'
                          : 'Brief description of the project'
                      }
                      multiline
                      onChange={handlePortfolioChange(i, 'description')}
                      value={el.description}
                      rows={3}
                    />
                  </Paper>
                </Stack>
              ))
            : portfolio.map((el, i) => (
                <Stack spacing={2} key={i} sx={{ my: 2 }}>
                  <Stack direction="row" spacing={2} sx={{ px: 2 }}>
                    <Box sx={{ mt: 0.3 }}>
                      <InsertLinkIcon />
                    </Box>
                    <a
                      href={`${el.link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {el.link}
                    </a>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ px: 2 }}>
                    <Box sx={{ mt: 0.3 }}>
                      <ArticleIcon color="primary" />
                    </Box>
                    <Typography color="text.secondary">
                      {el.description}
                    </Typography>
                  </Stack>
                  <Divider />
                </Stack>
              ))}

          {modeEditPortfolio ? (
            <Stack spacing={2} direction="row">
              <Box>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={onSubmitPortfolio}
                  startIcon={<SaveIcon />}
                >
                  {current === 'portfolio' && loading
                    ? locale === 'fr'
                      ? 'Chargement...'
                      : 'Loading...'
                    : locale === 'fr'
                    ? 'Enregistrer'
                    : 'Save'}
                </Button>
              </Box>
              <AddButton handleClick={addToPortfolio} />
            </Stack>
          ) : (
            <Box>
              <Button
                disableElevation
                onClick={() =>
                  dispatch(toggleEditMode({ modeEditPortfolio: true }))
                }
                startIcon={<EditIcon />}
              >
                {locale === 'fr' ? 'Editer' : 'Edit'}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack spacing={2} direction="row" alignItems="center">
            <AccountTreeIcon color="primary" />
            <Typography variant="h6" color="primary">
              {locale === 'fr' ? 'Disponibilité' : 'Availability'}
            </Typography>
            <Typography component="em" color="text.secondary" fontSize={13}>
              {locale === 'fr'
                ? 'Combien d’heures par jour pouvez-vous travailler s’il y a un projet'
                : 'How many hours per day can you work if there is a project'}
              )
            </Typography>
          </Stack>

          <Box sx={{ py: 4, px: { xs: 3, md: 6 } }}>
            <Slider
              value={hours || 37.5}
              step={12.5}
              onChange={handleChange}
              valueLabelDisplay="off"
              marks={marks}
            />
            <Stack direction="row" alignItems="center" sx={{ mt: 4 }}>
              <Typography variant="h6">
                {locale === 'fr'
                  ? 'Je suis disponible pour un job'
                  : 'I am available for a job'}
              </Typography>
              <Switch
                checked={available}
                onChange={handleChangeSwitch}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            </Stack>
          </Box>
          {modeEditAvailability ? (
            <Box>
              <Button
                variant="contained"
                disableElevation
                onClick={onSubmitAvailability}
                startIcon={<SaveIcon />}
              >
                {current === 'availability' && loading
                  ? locale === 'fr'
                    ? 'Chargement...'
                    : 'Loading...'
                  : locale === 'fr'
                  ? 'Enregistrer'
                  : 'Save'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Button
                disableElevation
                onClick={() =>
                  dispatch(toggleEditMode({ modeEditAvailability: true }))
                }
                startIcon={<EditIcon />}
              >
                {locale === 'fr' ? 'Editer' : 'Edit'}
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    </Root>
  );
};

export default Projects;
